import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Star, Handshake, Building2, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';

type AdminStatsResponse = {
  users: {
    total: number;
    byRole: Record<string, number>;
  };
  organizations: {
    total: number;
    agencies: number;
    brands: number;
  };
  kols: {
    total: number;
  };
  jobs: {
    total: number;
    active: number;
  };
  payments: {
    totalCount: number;
    totalAmountRM: number;
  };
  pending: {
    scripts: number;
    submissions: number;
    invitationChanges: number;
  };
};

type OrganizationItem = {
  id: string;
  name: string;
  type: 'BRAND' | 'AGENCY';
  createdAt: string;
  metrics: {
    memberCount: number;
    jobCount: number;
    totalBudgetRM: number;
    kolCount?: number;
    invitationCount?: number;
  };
};

type OrganizationsResponse = {
  items: OrganizationItem[];
  total: number;
};

const numberFmt = new Intl.NumberFormat('en-MY');

function orgTypeBadge(type: 'BRAND' | 'AGENCY') {
  if (type === 'AGENCY') {
    return (
      <span
        className="status-pill"
        style={{
          background: 'rgb(var(--accent-rgb) / 0.16)',
          color: 'rgb(var(--accent-rgb))',
        }}
      >
        Agency
      </span>
    );
  }
  return <span className="status-pill status-pill-info">Brand</span>;
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationItem[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoadingStats(true);
        setError(null);
        const { data } = await api.get<AdminStatsResponse>('/admin/stats');
        if (active) setStats(data);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } } };
        if (active) setError(e.response?.data?.error || 'Failed to load admin stats');
      } finally {
        if (active) setLoadingStats(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoadingOrgs(true);
        const { data } = await api.get<OrganizationsResponse>('/admin/organizations');
        if (active) setOrganizations(data.items || []);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { error?: string } } };
        if (active) setError((prev) => prev ?? (e.response?.data?.error || 'Failed to load organizations'));
      } finally {
        if (active) setLoadingOrgs(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, []);

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total Users', value: numberFmt.format(stats.users.total), icon: Users, link: '/admin/users' },
      { title: 'Total KOLs', value: numberFmt.format(stats.kols.total), icon: Star, link: '/admin/kols' },
      { title: 'Agencies', value: numberFmt.format(stats.organizations.agencies), icon: Handshake, link: '/admin/agencies' },
      { title: 'Brands', value: numberFmt.format(stats.organizations.brands), icon: Building2, link: '/admin/brands' },
    ];
  }, [stats]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title text-[1.65rem] sm:text-[1.85rem]">Admin Dashboard</h1>
        <p className="text-glass-text-2 mt-1 text-sm">Platform overview and organization management.</p>
      </div>

      {error && (
        <div className="glass-card p-4 border border-[rgb(var(--danger)/0.35)] text-sm text-[rgb(var(--danger))]">
          {error}
        </div>
      )}

      {loadingStats ? (
        <StatsGridSkeleton count={4} className="gap-4" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.title}
                to={stat.link}
                className="glass-card p-5 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.05em] font-medium text-glass-text-3">
                      {stat.title}
                    </p>
                    <div className="stat-display text-[1.85rem] text-glass-text mt-2 min-h-[2.25rem] flex items-center">
                      <StatNumber>{stat.value}</StatNumber>
                    </div>
                  </div>
                  <div
                    className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white shrink-0"
                    style={{
                      background:
                        'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.9), rgb(var(--accent2-rgb) / 0.7))',
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="glass-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[0.95rem] font-semibold text-glass-text">Organizations</h2>
          <span className="text-xs text-glass-text-3">
            {organizations.length} total
          </span>
        </div>

        <div className="overflow-x-auto rounded-[14px] border border-glass">
          <table className="w-full min-w-[640px]">
            <thead className="bg-glass-2 border-b border-glass">
              <tr>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Organization
                </th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Members
                </th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Campaigns
                </th>
                <th className="px-4 py-3 text-left text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-[0.65rem] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loadingOrgs ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-glass-text-2">
                    Loading organizations…
                  </td>
                </tr>
              ) : organizations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-glass-text-2">
                    No organizations found.
                  </td>
                </tr>
              ) : (
                organizations.slice(0, 12).map((org) => (
                  <tr key={org.id} className="hover:bg-glass-2 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-medium text-glass-text text-sm">{org.name}</div>
                      <div className="text-xs text-glass-text-3 font-mono truncate max-w-[200px]">{org.id}</div>
                    </td>
                    <td className="px-4 py-4">{orgTypeBadge(org.type)}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{org.metrics?.memberCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{org.metrics?.jobCount ?? 0}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-3">
                      {new Date(org.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {org.type === 'AGENCY' ? (
                        <Link
                          to={`/admin/agencies/${org.id}`}
                          className="text-xs font-semibold text-accent2 inline-flex items-center gap-1"
                        >
                          View <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      ) : (
                        <Link
                          to="/admin/brands"
                          className="text-xs font-semibold text-accent2 inline-flex items-center gap-1"
                        >
                          View <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {organizations.length > 12 && (
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/admin/agencies" className="text-xs font-semibold text-accent2">
              All agencies
            </Link>
            <Link to="/admin/brands" className="text-xs font-semibold text-accent2">
              All brands
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
